import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import { Box, AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, Breadcrumbs, CircularProgress, Icon } from '@mui/material';
import { ThemeProvider, CssBaseline } from '@mui/material';
import Footer from './components/Footer';
import { tibiaTheme, COLORS } from './theme/tibiaTheme';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { ApiStatusProvider, useApiStatus } from './contexts/ApiStatusContext';
import ErrorBoundary from './components/ErrorBoundary';
import ApiDownPage from './components/ApiDownPage';
import { APP_CONFIG } from './config/api';

// Lazy load das páginas
const Home = lazy(() => import('./pages/Home'));
const Deaths = lazy(() => import('./pages/Deaths'));
const Experience = lazy(() => import('./pages/Experience'));
const Bosses = lazy(() => import('./pages/Bosses'));

// Nome do site vem da configuração centralizada
const SITE_NAME = APP_CONFIG.APP_NAME;

// Mapa de rotas para breadcrumbs
const routeNames = {
  '/': 'Início',
  '/experience': 'Experiência',
  '/deaths': 'Mortes',
  '/bosses': 'Bosses',
};

function AppContent() {
  const { isApiDown } = useApiStatus();
  const [open, setOpen] = React.useState(false);
  const isMobile = useMediaQuery(tibiaTheme.breakpoints.down('md'));
  const location = useLocation();

  const toggleDrawer = () => {
    setOpen(prev => !prev);
  };

  const handleMenuClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  const menuItems = [
    { text: 'Início', path: '/', icon: <Icon className="material-symbols-outlined">home</Icon> },
    { text: APP_CONFIG.TERMS.EXPERIENCE, path: '/experience', icon: <Icon className="material-symbols-outlined">trending_up</Icon> },
    { text: APP_CONFIG.TERMS.DEATHS, path: '/deaths', icon: <Icon className="material-symbols-outlined">skull</Icon> },
    { text: 'Bosses', path: '/bosses', icon: <Icon className="material-symbols-outlined">star</Icon> },
  ];

  return (
    <>
      <Box sx={{ display: 'flex' }}>
          <AppBar 
            position="fixed" 
            sx={{
              width: isMobile ? '100%' : 'calc(100% - 260px)',
              ml: isMobile ? 0 : '260px',
            }}
          >
            <Toolbar 
              sx={{ 
                minHeight: '64px',
                backgroundColor: COLORS.surfaceContainer,
                borderBottom: `1px solid ${COLORS.outlineVariant}`,
                boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)',
              }}
            >
              {isMobile && (
                <IconButton 
                  color="inherit" 
                  edge="start" 
                  onClick={toggleDrawer}
                  aria-label="Abrir menu de navegação"
                  sx={{
                    color: 'onSurface.main',
                    mr: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(230, 225, 229, 0.08)',
                    }
                  }}
                >
                  <Icon className="material-symbols-outlined">menu</Icon>
                </IconButton>
              )}
              <Typography 
                variant="titleLarge" 
                sx={{ 
                  flexGrow: 1, 
                  fontWeight: 500,
                  color: 'onSurface.main',
                  letterSpacing: 0
                }}
              >
                {SITE_NAME}
              </Typography>
              <Breadcrumbs 
                separator={<Icon className="material-symbols-outlined" fontSize="small" sx={{ color: 'onSurfaceVariant.main' }}>navigate_next</Icon>}
                sx={{ color: 'onSurface.main' }}
                aria-label="Navegação de páginas"
              >
                <Typography variant="bodySmall" sx={{ color: 'onSurfaceVariant.main' }}>
                  Tracking
                </Typography>
                <Typography variant="bodySmall" sx={{ 
                  color: 'onSurface.main', 
                  fontWeight: 500,
                  letterSpacing: 0.25
                }}>
                  {routeNames[location.pathname] || 'Página'}
                </Typography>
              </Breadcrumbs>
            </Toolbar>
          </AppBar>

          <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={open}
            onClose={toggleDrawer}
            aria-label="Menu de navegação principal"
            sx={{
              width: 260,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: 260,
                height: '100vh',
                position: 'fixed',
                top: 0,
                left: 0,
                overflowX: 'hidden',
                overflowY: 'auto',
                boxSizing: 'border-box',
                backgroundColor: COLORS.surfaceContainer,
                borderRight: `1px solid ${COLORS.outlineVariant}`,
                boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
              },
            }}
          >
            <Box sx={{ 
              p: 2.5,
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'surfaceContainerHigh.main',
              borderBottom: `1px solid ${COLORS.outline}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="titleLarge" sx={{ 
                  color: 'onSurface.main', 
                  fontWeight: 500,
                  letterSpacing: 0
                }}>
                  {SITE_NAME}
                </Typography>
                <Typography variant="titleLarge" sx={{ 
                  color: 'onSurfaceVariant.main',
                  fontWeight: 500,
                  letterSpacing: 0
                }}>
                  -
                </Typography>
                <Typography variant="bodySmall" sx={{ 
                  color: 'onSurfaceVariant.main',
                  fontWeight: 500,
                  letterSpacing: 0.4
                }}>
                  Tibão System
                </Typography>
              </Box>
            </Box>
            
            {/* Divider removido para melhor aparência */}
            
            <List sx={{ pt: 1, px: 1 }}>
              {menuItems.map((item) => (
                <ListItem
                  key={item.text}
                  component={Link}
                  to={item.path}
                  onClick={handleMenuClick}
                  role="menuitem"
                  aria-label={`Navegar para ${item.text}`}
                  sx={{
                    color: 'onSurface.main',
                    mx: 1,
                    mb: 0.5,
                    borderRadius: '12px',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 0,
                      height: '60%',
                      backgroundColor: 'secondary.main',
                      borderRadius: '0 4px 4px 0',
                      transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(230, 225, 229, 0.08)',
                      transform: 'translateX(2px)',
                      '&::before': {
                        width: 3,
                      }
                    },
                    '&:active': {
                      backgroundColor: 'rgba(230, 225, 229, 0.12)',
                      transform: 'translateX(2px) scale(0.98)',
                      transition: 'all 0.1s ease',
                    }
                  }}
                >
                  <Box sx={{ 
                    mr: 2.5, 
                    color: 'onSurface.main',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    }
                  }}>
                    {item.icon}
                  </Box>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{
                      variant: 'labelLarge',
                      fontWeight: 500,
                      letterSpacing: 0.1
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Drawer>

          <Box
            component="main"
            sx={{ 
              flexGrow: 1, 
              p: 3, 
              pt: 11,
              pb: 10,
              backgroundColor: 'surface.main',
              minHeight: '100vh'
            }}
          >
            <Suspense 
              fallback={
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  minHeight: '60vh',
                  gap: 3
                }}>
                  <CircularProgress 
                    size={60} 
                    thickness={4}
                    sx={{ 
                      color: COLORS.secondary,
                      '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round',
                      }
                    }} 
                  />
                  <Typography variant="h6" sx={{ 
                    color: COLORS.onSurface, 
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    Carregando página...
                  </Typography>
                </Box>
              }
            >
              {isApiDown ? (
                <ApiDownPage />
              ) : (
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/deaths" element={<Deaths />} />
                  <Route path="/experience" element={<Experience />} />
                  <Route path="/bosses" element={<Bosses />} />
                </Routes>
              )}
            </Suspense>
          </Box>
        </Box>
        <Footer />
      </>
  );
}

function App() {
  return (
    <ThemeProvider theme={tibiaTheme}>
      <CssBaseline />
      <ErrorBoundary>
        <SnackbarProvider>
          <ApiStatusProvider>
            <Router>
              <AppContent />
            </Router>
          </ApiStatusProvider>
        </SnackbarProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;